/**
 * Talks to the Ardealy Team Chrome extension (extension/ardealy-team), whose
 * content script runs on the backoffice. window.postMessage both ways: the page
 * asks, the extension answers with its status. No answer means not installed.
 */
const FROM_PAGE = 'ardealy-backoffice';
const FROM_EXT = 'ardealy-team-extension';

export interface ExtensionStatus {
  version: string;
  connected: boolean;
  device: { id: string; label: string; owner: string } | null;
  /** 'reload': the extension was reloaded or updated since this page loaded, and cannot act here until the page is reloaded. */
  error?: string;
}

function ask(message: Record<string, unknown>, timeoutMs = 800): Promise<ExtensionStatus | null> {
  return new Promise((resolve) => {
    const done = (value: ExtensionStatus | null) => {
      window.removeEventListener('message', onMessage);
      clearTimeout(timer);
      resolve(value);
    };
    const onMessage = (e: MessageEvent) => {
      if (e.source !== window || e.origin !== window.location.origin) return;
      const data = e.data;
      if (data?.source === FROM_EXT && data.type === 'ARDEALY_TEAM_STATUS') {
        done({ version: data.version, connected: !!data.connected, device: data.device || null, error: data.error });
      }
    };
    const timer = setTimeout(() => done(null), timeoutMs);
    window.addEventListener('message', onMessage);
    window.postMessage({ source: FROM_PAGE, ...message }, window.location.origin);
  });
}

/** The extension's status, or null when it is not installed in this browser. */
export const pingExtension = () => ask({ type: 'ARDEALY_TEAM_PING' });

/** Hand this browser's device key to the extension. */
export const connectExtension = (key: string, device: { id: string; label: string; owner: string }) =>
  ask({ type: 'ARDEALY_TEAM_CONNECT', key, device }, 2000);

/** Take the key back from the extension. */
export const disconnectExtension = () => ask({ type: 'ARDEALY_TEAM_DISCONNECT' }, 2000);
